import { CalendarClock, Inbox, Sun, Sunrise } from 'lucide-react';

import { MenuItem } from '@/components/ui/menu';
import type { ScheduleTarget } from '@/lib/api/types';
import { cn } from '@/lib/utils';
import { useRef } from 'react';

import { useT } from '@/lib/i18n';

export type MoveHandler = (target: ScheduleTarget, date?: string) => void;

/**
 * MOVE TASK — Today · Tomorrow · pick a date · Backlog.
 *
 * These four are the entire vocabulary for moving a task (spec §23), and the
 * same four an overdue task offers. Defined once and rendered in two shapes, so
 * the choice can never differ between the row menu and the overdue banner.
 */
const TARGETS = [
  { target: 'today' as const, label: 'move.today' as const, Icon: Sun },
  { target: 'tomorrow' as const, label: 'move.tomorrow' as const, Icon: Sunrise },
];

/** Rendered inside an open dropdown menu. */
export function MoveMenuItems({
  onMove,
  defaultDate,
}: {
  onMove: MoveHandler;
  defaultDate: string;
}) {
  const t = useT();
  return (
    <>
      {TARGETS.map(({ target, label, Icon }) => (
        <MenuItem key={target} onSelect={() => onMove(target)}>
          <Icon className="h-3.5 w-3.5 text-content-subtle" aria-hidden />
          {t(label)}
        </MenuItem>
      ))}

      <MenuItem asChild onSelect={(event) => event.preventDefault()}>
        <DatePicker defaultDate={defaultDate} onPick={(date) => onMove('date', date)}>
          <span className="flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm">
            <CalendarClock className="h-3.5 w-3.5 text-content-subtle" aria-hidden />
            {t('move.pickDate')}
          </span>
        </DatePicker>
      </MenuItem>

      <MenuItem onSelect={() => onMove('backlog')}>
        <Inbox className="h-3.5 w-3.5 text-content-subtle" aria-hidden />
        {t('move.backlog')}
      </MenuItem>
    </>
  );
}

/**
 * The same four choices as inline buttons.
 *
 * Used on the overdue banner, where the whole point is that resolving a slipped
 * task takes one visible click — not a click to open a menu and then another.
 */
export function MoveButtons({
  onMove,
  defaultDate,
  className,
}: {
  onMove: MoveHandler;
  defaultDate: string;
  className?: string;
}) {
  const t = useT();
  const buttonClass = cn(
    'rounded border border-overdue-border bg-surface px-2 py-1 text-xs font-medium',
    'text-content transition-surface hover:bg-surface-hover',
  );

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {TARGETS.map(({ target, label }) => (
        <button
          key={target}
          type="button"
          onClick={() => onMove(target)}
          className={buttonClass}
        >
          {t(label)}
        </button>
      ))}

      <DatePicker defaultDate={defaultDate} onPick={(date) => onMove('date', date)}>
        <span className={cn(buttonClass, 'cursor-pointer')}>{t('move.pickDate')}</span>
      </DatePicker>

      <button type="button" onClick={() => onMove('backlog')} className={buttonClass}>
        {t('move.backlog')}
      </button>
    </div>
  );
}

/**
 * The platform date picker, actually opened.
 *
 * A `<label>` wrapping a hidden `<input type="date">` looks like it should
 * work, and does nothing: clicking a label focuses the input, but browsers only
 * drop the calendar down when the *indicator* inside the field is clicked.
 * `showPicker()` is the API that opens it, and it must be called straight from
 * the click — a `setTimeout` or an `await` in between loses the user gesture
 * and the browser refuses.
 *
 * The input stays in the DOM and merely invisible. `display: none` would make
 * `showPicker()` throw, and removing it would leave nothing to ask.
 */
function DatePicker({
  defaultDate,
  onPick,
  children,
}: {
  defaultDate: string;
  onPick: (date: string) => void;
  children: React.ReactNode;
}) {
  const input = useRef<HTMLInputElement>(null);

  function open() {
    const field = input.current;
    if (!field) return;
    try {
      field.showPicker();
    } catch {
      // Older Safari has no `showPicker`. Focusing at least reaches the field,
      // which can then be typed into.
      field.focus();
    }
  }

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      }}
      className="relative block"
    >
      {children}
      <input
        ref={input}
        type="date"
        defaultValue={defaultDate}
        tabIndex={-1}
        aria-hidden
        // Invisible but present and laid out, so the picker has something to
        // anchor to and `showPicker()` does not throw.
        className="pointer-events-none absolute bottom-0 left-2 h-0 w-0 opacity-0"
        onChange={(event) => {
          if (event.target.value) onPick(event.target.value);
        }}
      />
    </span>
  );
}
